/**
 * Template Registry
 *
 * Central registry for all template modules.
 * To add a new template:
 * 1. Create a folder in templates/ with your template module
 * 2. Import and add it to the registry below
 *
 * That's it! The template will automatically appear in the UI.
 */

import { TemplateModule, TemplateRegistry, TemplateDefinition, TemplateConfig, TemplateCategory } from './types';

// Import all template modules
import { relatedSearchesTemplate } from './templates/related-searches';
import { relatedVideosCarouselTemplate } from './templates/related-videos-carousel';

// ============================================================================
// TEMPLATE REGISTRY
// ============================================================================

/**
 * The central registry of all available templates.
 * Add new templates here after creating their module.
 */
export const templateRegistry: TemplateRegistry = {
  'related-searches': relatedSearchesTemplate,
  'related-videos-carousel': relatedVideosCarouselTemplate,
  // Add new templates here:
  // 'vertical-link-list': verticalLinkListTemplate,
  // 'image-card-grid': imageCardGridTemplate,
};

// ============================================================================
// DERIVED TYPES & HELPERS
// ============================================================================

/**
 * TemplateType is automatically derived from the registry keys.
 * No need to manually maintain a union type!
 */
export type TemplateType = keyof typeof templateRegistry;

/**
 * Get all template IDs
 */
export const getTemplateIds = (): TemplateType[] => {
  return Object.keys(templateRegistry) as TemplateType[];
};

/**
 * Get a template module by ID
 */
export const getTemplate = (id: string): TemplateModule | undefined => {
  return templateRegistry[id];
};

/**
 * Get template definition by ID
 */
export const getTemplateDefinition = (id: string): TemplateDefinition | undefined => {
  return templateRegistry[id]?.definition;
};

/**
 * Get all template definitions
 */
export const getAllDefinitions = (): TemplateDefinition[] => {
  return Object.values(templateRegistry).map((t) => t.definition);
};

/**
 * Get templates by category
 */
export const getTemplatesByCategory = (category: TemplateCategory | 'all'): TemplateDefinition[] => {
  const definitions = getAllDefinitions();
  if (category === 'all') return definitions;
  return definitions.filter((d) => d.category === category);
};

/**
 * Get default config for a template
 */
export const getDefaultConfig = (id: string): TemplateConfig => {
  const template = templateRegistry[id];
  if (!template) return {};

  const config: TemplateConfig = {};
  template.definition.fields.forEach((field) => {
    config[field.name] = field.default;
  });
  return config;
};

/**
 * Get the Preview component for a template
 */
export const getPreviewComponent = (id: string) => {
  return templateRegistry[id]?.Preview;
};

/**
 * Generate HTML for a template
 */
export const generateTemplateHtml = (id: string, config: TemplateConfig): string => {
  const template = templateRegistry[id];
  if (!template) return '<!-- Unknown template -->';
  return template.generateHtml(config);
};

/**
 * Generate preview HTML for a template
 */
export const generateTemplatePreviewHtml = (id: string, config: TemplateConfig): string => {
  const template = templateRegistry[id];
  if (!template) return '<!-- Unknown template -->';
  return template.generatePreviewHtml(config);
};

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate that all templates in the registry are complete.
 * Run this at build time to catch missing pieces early.
 */
export const validateRegistry = (): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  Object.entries(templateRegistry).forEach(([id, template]) => {
    if (!template.definition) {
      errors.push(`${id}: missing definition`);
    } else {
      if (!template.definition.id) errors.push(`${id}: definition missing id`);
      if (!template.definition.name) errors.push(`${id}: definition missing name`);
      if (!template.definition.category) errors.push(`${id}: definition missing category`);
      if (!template.definition.fields) errors.push(`${id}: definition missing fields`);
    }
    if (!template.Preview) errors.push(`${id}: missing Preview component`);
    if (!template.generateHtml) errors.push(`${id}: missing generateHtml function`);
    if (!template.generatePreviewHtml) errors.push(`${id}: missing generatePreviewHtml function`);
  });

  return { valid: errors.length === 0, errors };
};

// Re-export types for convenience
export type { TemplateModule, TemplateDefinition, TemplateConfig, TemplateCategory, TemplateField } from './types';
export type { PreviewProps, SampleData } from './types';
